/**
 * Internationalisation — catalogues externes, façade inchangée.
 *
 * CE QUI CHANGE, ET CE QUI NE CHANGE PAS
 * --------------------------------------
 * Les textes vivaient à deux endroits inconciliables : le français DISPERSÉ dans le code, en
 * second argument de chacun des 1 239 appels, et l'anglais réparti entre deux objets d'un même
 * module dont l'un s'étalait au milieu de l'autre — la traduction retenue dépendait de l'ordre
 * des lignes. Aucun traducteur ne pouvait travailler là-dessus, et aucun outil ne pouvait dire
 * ce qui manquait.
 *
 * Les deux catalogues sont désormais dans `locales/`, extraits du code par analyse syntaxique
 * (`scripts/extraire-i18n.cjs`), donc reproductibles et vérifiables en intégration continue.
 *
 * **La signature `t(clé, français)` est conservée.** Les 1 239 appels et les 67 fichiers qui
 * l'utilisent ne bougent pas. Le second argument devient un repli de dernier recours plutôt que
 * la source du texte : si le catalogue connaît la clé, c'est lui qui parle. Une migration qui
 * exigerait de réécrire 67 fichiers ne se ferait jamais.
 *
 * CE QUE LA FAÇADE APPORTE EN PLUS
 * --------------------------------
 * - **interpolation** : `t('salut', 'Bonjour {{nom}}', { nom })` — impossible auparavant, ce qui
 *   obligeait à concaténer des fragments, forme intraduisible dès que l'ordre des mots change ;
 * - **pluriel** : `t('n.items', '{{n}} élément', { n })` choisit `n.items_zero|_one|_other` ;
 * - **dates, heures et nombres localisés** via `Intl` — aucune n'était localisée : les dates
 *   étaient tronquées à la main (`slice(0, 10)`), ce qui impose un format à tous les pays.
 *
 * Aucune dépendance ajoutée : ces trois besoins tiennent en une centaine de lignes, là où un
 * cadriciel apporterait surtout ce dont ce produit n'a pas l'usage (espaces de noms, chargement
 * différé, détection de langue par requête).
 */
import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState,
  type JSX, type ReactNode,
} from 'react'

import { CATALOGUES_FUSIONNES } from './fragments'

export type Lang = 'fr' | 'en'
const STORAGE_KEY = 'doit.lang'
const LANGUAGE_CHANGE_EVENT = 'doit:language-change'

/**
 * Seules les locales que le produit et le realm importé savent rendre peuvent
 * franchir le contrat OIDC. Une valeur de navigateur, d'URL ou de claim inconnue
 * ne traverse donc jamais vers Keycloak : le repli produit est explicitement `fr`.
 */
export function localeSupportee(valeur: unknown): Lang | null {
  if (typeof valeur !== 'string') return null
  const primaire = valeur.trim().toLowerCase().split(/[-_]/, 1)[0]
  return primaire === 'fr' || primaire === 'en' ? primaire : null
}

export function normaliserLocale(valeur: unknown): Lang {
  return localeSupportee(valeur) ?? 'fr'
}

function localeKeycloakDansUrl(): Lang | null {
  try {
    return localeSupportee(new URLSearchParams(window.location.search).get('kc_locale'))
  } catch {
    return null
  }
}

/** Valeurs interpolables. `n` pilote en outre la forme plurielle. */
export type Variables = Record<string, string | number>

const CATALOGUES: Record<Lang, Record<string, string>> = CATALOGUES_FUSIONNES

/** Étiquettes de langue au format BCP 47, pour `Intl`. */
const ETIQUETTE: Record<Lang, string> = { fr: 'fr-FR', en: 'en-US' }

/**
 * Remplace `{{variable}}`. Une variable absente laisse son marqueur VISIBLE plutôt que de
 * produire « Bonjour  » : un texte manifestement cassé se corrige, un texte discrètement
 * amputé se diffuse.
 */
function interpoler(modele: string, variables?: Variables): string {
  if (!variables) return modele
  return modele.replace(/\{\{(\w+)\}\}/g, (marqueur, nom: string) => {
    const valeur = variables[nom]
    return valeur === undefined ? marqueur : String(valeur)
  })
}

/**
 * Forme plurielle selon la langue : `clé_zero`, `clé_one`, `clé_other`.
 *
 * Les règles diffèrent d'une langue à l'autre — en français 0 et 1 prennent le singulier, en
 * anglais 0 prend le pluriel. `Intl.PluralRules` porte cette connaissance ; la coder à la main
 * reviendrait à la refaire pour chaque langue ajoutée.
 */
function clePlurielle(cle: string, lang: Lang, n: number,
                      catalogue: Record<string, string>): string | null {
  if (n === 0 && catalogue[`${cle}_zero`]) return `${cle}_zero`
  const categorie = new Intl.PluralRules(ETIQUETTE[lang]).select(n)
  const candidate = `${cle}_${categorie}`
  if (catalogue[candidate]) return candidate
  return catalogue[`${cle}_other`] ? `${cle}_other` : null
}

interface LangValue {
  lang: Lang
  setLang: (l: Lang) => void
  /** Traduit. `repli` reste le second argument : la signature historique est préservée. */
  t: (key: string, repli?: string, variables?: Variables) => string
  /** Date localisée. Accepte une chaîne ISO, un horodatage ou une `Date`. */
  formatDate: (valeur: string | number | Date, options?: Intl.DateTimeFormatOptions) => string
  /** Nombre localisé (séparateurs de milliers, décimales, unités). */
  formatNombre: (valeur: number, options?: Intl.NumberFormatOptions) => string
}

const Ctx = createContext<LangValue | null>(null)

/**
 * Langue courante, lisible HORS React.
 *
 * Exportée parce que le retour des mutations est décidé dans un `MutationCache` global
 * (`lib/mutation-feedback.ts`), qui vit à côté du `QueryClient` et non dans l'arbre React :
 * il n'a donc pas accès à `useT()`. Sans cet export, tous les toasts par défaut seraient
 * en français, y compris pour un utilisateur ayant choisi l'anglais.
 */
export function langueCourante(): Lang {
  try {
    // `kc_locale` est le retour explicite Keycloak → SPA lorsqu'il est présent.
    // Sinon la préférence déjà négociée reste l'autorité pendant le callback OIDC.
    return localeKeycloakDansUrl() ?? normaliserLocale(localStorage.getItem(STORAGE_KEY))
  } catch {
    return 'fr'
  }
}

/** Synchronise une locale décidée hors React (callback OIDC) avec le provider. */
export function definirLangueCourante(valeur: unknown): Lang {
  const lang = normaliserLocale(valeur)
  try {
    localStorage.setItem(STORAGE_KEY, lang)
    window.dispatchEvent(new Event(LANGUAGE_CHANGE_EVENT))
  } catch { /* stockage ou DOM indisponible : le repli reste déterministe */ }
  return lang
}

/**
 * RJ-08 (rejeu caisse) : la langue repassait en anglais à chaque connexion — le callback
 * applique la locale du PROFIL Keycloak et écrasait le choix fait dans l'interface. Un choix
 * EXPLICITE (sélecteur de langue) est désormais mémorisé et prime sur le profil.
 */
const CHOIX_EXPLICITE_KEY = 'doit.lang.explicite'

/** Choix explicite de l'utilisateur : mémorisé, il prime sur la locale du profil Keycloak. */
export function choisirLangue(valeur: unknown): Lang {
  const lang = definirLangueCourante(valeur)
  try { localStorage.setItem(CHOIX_EXPLICITE_KEY, lang) } catch { /* stockage indisponible */ }
  return lang
}

/** La langue choisie explicitement dans l'interface, ou `null` si aucun choix n'a été fait. */
export function langueChoisie(): Lang | null {
  try { return localeSupportee(localStorage.getItem(CHOIX_EXPLICITE_KEY)) } catch { return null }
}

/** Première langue prise en charge parmi celles du navigateur (`navigator.languages`), ou `null`. */
export function langueNavigateur(): Lang | null {
  try {
    const candidates = typeof navigator === 'undefined' ? []
      : [...(navigator.languages ?? []), navigator.language].filter((v): v is string => typeof v === 'string')
    for (const c of candidates) {
      const l = localeSupportee(c)
      if (l) return l
    }
  } catch { /* navigateur sans langues déclarées : repli déterministe */ }
  return null
}

/**
 * Lot NAV 0.5.x (A2, écran de connexion en anglais sur un navigateur fr-FR) : AVANT la connexion,
 * personne n'est identifié — la langue déposée par le PROFIL Keycloak du compte précédent (callback
 * OIDC) ne décrit pas la personne devant l'écran. Ordre : choix explicite fait dans l'interface,
 * langue du navigateur, français.
 */
export function langueAvantConnexion(): Lang {
  return langueChoisie() ?? langueNavigateur() ?? 'fr'
}

/** Traduction pure, hors React : utilisable dans les tests et hors composant. */
export function traduire(lang: Lang, cle: string, repli?: string,
                         variables?: Variables): string {
  const catalogue = CATALOGUES[lang]
  let cleEffective = cle

  if (variables && typeof variables.n === 'number') {
    const pluriel = clePlurielle(cle, lang, variables.n, catalogue)
    if (pluriel) cleEffective = pluriel
  }

  // Ordre de repli : catalogue de la langue → français (jamais une clé nue à l'écran) →
  // texte passé par l'appelant → la clé elle-même, en dernier recours visible.
  const texte = catalogue[cleEffective]
    ?? (lang !== 'fr' ? CATALOGUES.fr[cleEffective] : undefined)
    ?? repli
    ?? cle

  return interpoler(texte, variables)
}

export function LanguageProvider({ children }: { children: ReactNode }): JSX.Element {
  const [lang, setLangState] = useState<Lang>(() => langueCourante())

  const setLang = useCallback((l: Lang) => {
    const normalisee = choisirLangue(l)
    setLangState(normalisee)
  }, [])

  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  // Lot NAV 0.5.x : abonnement en effet de MISE EN PAGE — il est posé avant les effets ordinaires
  // des enfants montés dans le même rendu (l'écran de connexion fixe sa langue dans le sien) ; en
  // effet ordinaire, l'événement partait avant l'abonnement et la langue n'était pas suivie.
  useLayoutEffect(() => {
    const synchroniser = () => setLangState(langueCourante())
    window.addEventListener(LANGUAGE_CHANGE_EVENT, synchroniser)
    return () => window.removeEventListener(LANGUAGE_CHANGE_EVENT, synchroniser)
  }, [])

  const t = useCallback(
    (key: string, repli?: string, variables?: Variables) => traduire(lang, key, repli, variables),
    [lang])

  const formatDate = useCallback(
    (valeur: string | number | Date, options?: Intl.DateTimeFormatOptions) => {
      const d = valeur instanceof Date ? valeur : new Date(valeur)
      if (Number.isNaN(d.getTime())) return String(valeur)  // entrée illisible : rendue telle quelle
      return new Intl.DateTimeFormat(ETIQUETTE[lang],
        options ?? { dateStyle: 'medium' }).format(d)
    }, [lang])

  const formatNombre = useCallback(
    (valeur: number, options?: Intl.NumberFormatOptions) =>
      new Intl.NumberFormat(ETIQUETTE[lang], options).format(valeur),
    [lang])

  const value = useMemo(
    () => ({ lang, setLang, t, formatDate, formatNombre }),
    [lang, setLang, t, formatDate, formatNombre])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/** Hook de traduction : `const { t, lang, setLang, formatDate } = useT()`. */
export function useT(): LangValue {
  const v = useContext(Ctx)
  if (!v) {
    // Hors provider : le français du catalogue, sans état. Un composant isolé dans un test
    // doit rendre du texte lisible, pas des clés.
    return {
      lang: 'fr',
      setLang: () => {},
      t: (k, repli, variables) => traduire('fr', k, repli, variables),
      formatDate: (valeur, options) => {
        const d = valeur instanceof Date ? valeur : new Date(valeur)
        return Number.isNaN(d.getTime())
          ? String(valeur)
          : new Intl.DateTimeFormat(ETIQUETTE.fr, options ?? { dateStyle: 'medium' }).format(d)
      },
      formatNombre: (valeur, options) => new Intl.NumberFormat(ETIQUETTE.fr, options).format(valeur),
    }
  }
  return v
}
